import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Icon } from './Icons';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { listConversations, subscribe, saveActiveId, loadActiveId, loadConversations, saveConversations } from '../utils/chatStore';
import { ADMIN_PAGE_ROLES } from '../auth/constants';

/**
 * Collapsible left sidebar — the Copilot shape: a new-chat action, the app's capabilities
 * ("talents"), then chat history, with account actions pinned at the bottom.
 *
 * This replaces both the old floating ChatFab and the right-hand CopilotPanel, and it is
 * now the app's primary navigation: the horizontal header nav is gone. Chat itself happens
 * on /chat, so "New chat" and any history entry navigate there.
 *
 * The history list subscribes to utils/chatStore, so a message sent on the chat page shows
 * up here immediately rather than on the next remount.
 */

const HISTORY_OPEN_KEY = 'bagent-sidebar-history-open';

/** Fold Turkish casing so "İhtar", "ihtar" and "IHTAR" all match one another. */
function normalizeTr(v) {
  return String(v ?? '')
    .replace(/İ/g, 'i').replace(/I/g, 'ı')
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i');
}
const TALENTS_OPEN_KEY = 'bagent-sidebar-talents-open';
const HISTORY_LIMIT = 15;

/** The capabilities, in the order they appeared in the old header nav. */
const TALENTS = [
  { to: '/contract', labelKey: 'nav.contractAnalysis', icon: 'shield' },
  { to: '/diff', labelKey: 'nav.legislationDiff', icon: 'compare' },
  { to: '/caselaw', labelKey: 'nav.caseLaw', icon: 'gavel' },
  // Bakanlık Kararları is a tab inside İçtihat / Emsal; a second entry here pointed at the
  // same search and only made the list longer. /ministry still routes for old links.
  { to: '/generator', labelKey: 'nav.generator', icon: 'file' },
  { to: '/donustur', labelKey: 'nav.converter', icon: 'convert' },
  { to: '/hesaplama', labelKey: 'nav.hesaplama', icon: 'calculator' },
  { to: '/dosyalar', labelKey: 'nav.caseFiles', icon: 'folder' },
];




/** Both sections default to open; a stored '0' means the user collapsed it. */
function loadSectionOpen(key) {
  try {
    return localStorage.getItem(key) !== '0';
  } catch {
    return true;
  }
}

/** Collapsible section heading with a rotating chevron. */
function SectionHead({ label, open, onToggle, showLabel, hideLabel }) {
  return (
    <div className="sidebar-section-head">
      <span className="sidebar-section-label">{label}</span>
      <button
        type="button"
        className="sidebar-section-toggle"
        onClick={onToggle}
        aria-expanded={open}
        title={open ? hideLabel : showLabel}
        aria-label={open ? hideLabel : showLabel}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
          style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
    </div>
  );
}

export default function CopilotSidebar({ onNavigate }) {
  const { t } = useLanguage();
  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    // Local/mock logout clears the session without redirecting (the Entra one redirects
    // itself). Without this a signed-out user sat on the page they were already on.
    navigate('/login');
  };

  const [conversations, setConversations] = useState(listConversations);
  const [historyOpen, setHistoryOpen] = useState(() => loadSectionOpen(HISTORY_OPEN_KEY));
  const [chatQuery, setChatQuery] = useState('');

  /**
   * Filter threads by title and by message text. Turkish-insensitive: the dotted/dotless i
   * pair means a plain toLowerCase() makes "İhtar" and "ihtar" fail to match each other.
   */
  const filteredConversations = useMemo(() => {
    const q = normalizeTr(chatQuery);
    if (!q) return conversations;
    return conversations.filter((c) => {
      if (normalizeTr(c.title || '').includes(q)) return true;
      return (c.messages || []).some((m) => normalizeTr(m?.content || '').includes(q));
    });
  }, [conversations, chatQuery]);
  const [talentsOpen, setTalentsOpen] = useState(() => loadSectionOpen(TALENTS_OPEN_KEY));
  const [activeChatId, setActiveChatId] = useState(loadActiveId);

  const refresh = useCallback(() => {
    setConversations(listConversations());
    setActiveChatId(loadActiveId());
  }, []);
  useEffect(() => subscribe(refresh), [refresh]);

  useEffect(() => {
    try { localStorage.setItem(HISTORY_OPEN_KEY, historyOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [historyOpen]);

  useEffect(() => {
    try { localStorage.setItem(TALENTS_OPEN_KEY, talentsOpen ? '1' : '0'); } catch { /* ignore */ }
  }, [talentsOpen]);

  const openConversation = (id) => {
    saveActiveId(id);
    // The id rides in the query string as well, so the chat page switches threads even
    // when it is already mounted and would not re-read localStorage on its own.
    navigate(`/chat?c=${encodeURIComponent(id)}`);
    onNavigate?.();
  };

  const handleNewChat = () => {
    saveActiveId(null);
    navigate('/chat?new=1');
    onNavigate?.();
  };

  const handleDelete = (id, e) => {
    e.stopPropagation();
    saveConversations(loadConversations().filter((c) => c.id !== id));
  };

  return (
    <nav className="app-sidebar" aria-label={t('nav.mainNav')}>
      <div className="sidebar-scroll">
        <button type="button" className="sidebar-newchat" onClick={handleNewChat}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v8M8 12h8" />
          </svg>
          <span>{t('chat.newChat')}</span>
        </button>

        <NavLink to="/panel" className="sidebar-link" onClick={onNavigate}>
          <Icon name="dashboard" />
          <span>{t('nav.dashboard')}</span>
        </NavLink>
        <NavLink to="/chat" className="sidebar-link" onClick={onNavigate}>
          <Icon name="chat" />
          <span>{t('nav.chat')}</span>
        </NavLink>

        <SectionHead
          label={t('sidebar.talents')}
          open={talentsOpen}
          onToggle={() => setTalentsOpen((v) => !v)}
          showLabel={t('sidebar.showSection')}
          hideLabel={t('sidebar.hideSection')}
        />
        {talentsOpen && TALENTS.map((item) => (
          <NavLink key={item.to} to={item.to} className="sidebar-link" onClick={onNavigate}>
            <Icon name={item.icon} />
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}

        <SectionHead
          label={t('sidebar.chats')}
          open={historyOpen}
          onToggle={() => setHistoryOpen((v) => !v)}
          showLabel={t('sidebar.showSection')}
          hideLabel={t('sidebar.hideSection')}
        />
        {historyOpen && conversations.length > 0 && (
          <div className="sidebar-chat-search">
            <input
              type="search"
              className="sidebar-chat-search-input"
              value={chatQuery}
              onChange={(e) => setChatQuery(e.target.value)}
              placeholder={t('sidebar.searchChats')}
              aria-label={t('sidebar.searchChats')}
            />
          </div>
        )}
        {historyOpen && (
          conversations.length === 0 ? (
            <p className="sidebar-empty">{t('chat.noHistory')}</p>
          ) : filteredConversations.length === 0 ? (
            <p className="sidebar-empty">{t('sidebar.noChatMatch')}</p>
          ) : (
            filteredConversations.slice(0, HISTORY_LIMIT).map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                className={`sidebar-chat-item${c.id === activeChatId ? ' active' : ''}`}
                onClick={() => openConversation(c.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openConversation(c.id); }}
                title={c.title || t('chat.newChat')}
              >
                <span className="sidebar-chat-title">{c.title || t('chat.newChat')}</span>
                <button
                  type="button"
                  className="sidebar-chat-delete"
                  onClick={(e) => handleDelete(c.id, e)}
                  title={t('chat.deleteConvo')}
                  aria-label={t('chat.deleteConvo')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </div>
            ))
          )
        )}
      </div>

      <div className="sidebar-footer">
        {ADMIN_PAGE_ROLES.includes(user?.role) && (
          <NavLink to="/admin" className="sidebar-link" onClick={onNavigate}>
            <Icon name="admin" />
            <span>{t('nav.admin')}</span>
          </NavLink>
        )}
        <NavLink to="/settings" className="sidebar-link" onClick={onNavigate}>
          <Icon name="settings" />
          <span>{t('nav.settings')}</span>
        </NavLink>
        <div className="sidebar-user">
          <span className="sidebar-avatar" aria-hidden>
            {(user?.name || user?.email || 'B').slice(0, 1).toUpperCase()}
          </span>
          <span className="sidebar-user-meta">
            <span className="sidebar-user-name">{user?.name || user?.email || ''}</span>
            <span className="sidebar-user-role">{user?.role || 'Legal Analyst'}</span>
          </span>
          {/* Sign out belongs with the identity it signs out of, not stranded in the
              header next to the language and theme controls. */}
          <button
            type="button"
            className="sidebar-signout"
            onClick={handleSignOut}
            title={t('auth.logOut')}
            aria-label={t('auth.logOut')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5M21 12H9" />
            </svg>
          </button>
        </div>
      </div>
    </nav>
  );
}
